import React, { useEffect, useRef, useState } from 'react';


const WS_BASE_URL = process.env.REACT_APP_WS_BASE_URL;
const InstitutionMonitoringMain = ({ examId }) => {
    const [students, setStudents] = useState({});
    const socketRef = useRef(null);
    const pcsRef = useRef({}); // student_id -> RTCPeerConnection
    const videoRefsRef = useRef({}); // student_id -> video DOM element

    // Empty iceServers: forces direct local host candidates for localhost testing.
    // Edge Tracking Prevention blocks STUN/TURN servers, causing ICE to hang.
    // For production deployment, restore: [{ urls: 'stun:stun.l.google.com:19302' }]
    const configuration = { iceServers: [] };

    useEffect(() => {
        let mounted = true;

        const connect = () => {
            if (!mounted) return;

            const wsUrl = `${WS_BASE_URL}/ws/webrtc/${examId}/`;
            const socket = new WebSocket(wsUrl);
            socketRef.current = socket;

            socket.onopen = () => {
                if (!mounted) { socket.close(); return; }
                console.log('Monitoring WebSocket connected');
                socket.send(JSON.stringify({ type: 'join', role: 'monitor' }));
            };

            socket.onmessage = async (event) => {
                if (!mounted) return;
                const data = JSON.parse(event.data);
                const { type, student_id, student_name, offer, candidate } = data;
                if (type === 'offer') handleOffer(student_id, student_name, offer);
                else if (type === 'ice_candidate' && student_id) handleIceCandidate(student_id, candidate);
                else if (type === 'leave') handleStudentLeave(student_id);
            };

            socket.onclose = () => {
                if (!mounted) return; // Don't reconnect after unmount
                console.log('Monitoring WebSocket disconnected, retrying in 3s...');
                setTimeout(connect, 3000);
            };

            socket.onerror = (err) => console.error('Monitoring WebSocket error:', err);
        };

        connect();

        return () => {
            mounted = false;
            const socket = socketRef.current;
            if (socket) {
                // Only close if past CONNECTING state to avoid the "closed before established" error
                if (socket.readyState !== WebSocket.CONNECTING) {
                    socket.close();
                } else {
                    // Let it connect then immediately close
                    socket.onopen = () => socket.close();
                }
            }
            Object.values(pcsRef.current).forEach(pc => pc.close());
            pcsRef.current = {};
        };
    }, [examId]);


    const handleOffer = async (studentId, studentName, offer) => {
        console.log(`Received offer from ${studentName} (${studentId})`);

        let pc = pcsRef.current[studentId];

        // Reuse existing peer connection if still healthy
        if (pc && (pc.connectionState === 'connected' || pc.connectionState === 'connecting')) {
            console.log(`Reusing existing PC for student ${studentId}`);
            return;
        }

        if (pc) pc.close();

        pc = new RTCPeerConnection(configuration);
        pcsRef.current[studentId] = pc;

        setStudents(prev => ({
            ...prev,
            [studentId]: { ...prev[studentId], name: studentName, status: 'Connecting' }
        }));

        pc.onicecandidate = (event) => {
            if (event.candidate && socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({
                    type: 'ice_candidate',
                    candidate: event.candidate,
                    student_id: studentId
                }));
            }
        };

        pc.ontrack = (event) => {
            console.log(`Received track from ${studentId}`);
            const stream = event.streams[0];

            // Directly set srcObject on the video DOM element to bypass React state timing
            const videoEl = videoRefsRef.current[studentId];
            if (videoEl) {
                videoEl.srcObject = stream;
                videoEl.play().catch(e => console.warn('Autoplay blocked:', e));
            }

            setStudents(prev => ({
                ...prev,
                [studentId]: { ...prev[studentId], status: 'Live', stream }
            }));
        };

        pc.onconnectionstatechange = () => {
            const state = pc.connectionState;
            console.log(`Connection state for ${studentId}: ${state}`);
            if (state === 'disconnected' || state === 'failed' || state === 'closed') {
                setStudents(prev => {
                    const next = { ...prev };
                    if (next[studentId]) next[studentId] = { ...next[studentId], status: 'Disconnected' };
                    return next;
                });
            }
        };

        try {
            await pc.setRemoteDescription(new RTCSessionDescription(offer));
            const answer = await pc.createAnswer();
            await pc.setLocalDescription(answer);
            if (socketRef.current?.readyState === WebSocket.OPEN) {
                socketRef.current.send(JSON.stringify({ type: 'answer', answer, student_id: studentId }));
            }
        } catch (err) {
            console.error(`Error handling offer for student ${studentId}:`, err);
        }
    };

    const handleIceCandidate = async (studentId, candidate) => {
        const pc = pcsRef.current[studentId];
        if (pc && candidate) {
            try { await pc.addIceCandidate(new RTCIceCandidate(candidate)); }
            catch (e) { console.error('Error adding ice candidate', e); }
        }
    };

    const handleStudentLeave = (studentId) => {
        if (!studentId) return;
        console.log(`Student ${studentId} left`);
        setStudents(prev => { const next = { ...prev }; delete next[studentId]; return next; });
        if (pcsRef.current[studentId]) {
            pcsRef.current[studentId].close();
            delete pcsRef.current[studentId];
        }
    };

    return (
        <div className="monitoring-grid">
            <h2 className="mb-4">Live Monitoring - Exam ID: {examId}</h2>
            <div className="row">
                {Object.keys(students).length === 0 ? (
                    <div className="col-12 text-center py-5">
                        <div className="card shadow-sm border-0 p-5">
                            <i className="fas fa-video-slash fa-3x mb-3 text-muted"></i>
                            <h4 className="text-muted">Waiting for students to join...</h4>
                        </div>
                    </div>
                ) : (
                    Object.entries(students).map(([id, student]) => (
                        <div key={id} className="col-12 col-sm-6 col-lg-4 col-xl-3 mb-4">
                            <StudentVideoCard
                                id={id}
                                student={student}
                                videoRefCallback={(el) => { videoRefsRef.current[id] = el; }}
                            />
                        </div>
                    ))
                )}
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                .monitoring-grid { padding: 20px 0; }
                .student-card {
                    background: #fff; border-radius: 12px; overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
                    transition: transform 0.3s ease; border: 1px solid #eee;
                }
                .student-card:hover { transform: translateY(-5px); }
                .video-container {
                    position: relative; width: 100%; padding-top: 75%; background: #000;
                }
                .student-video {
                    position: absolute; top: 0; left: 0;
                    width: 100%; height: 100%; object-fit: cover;
                }
                .video-overlay {
                    position: absolute; top: 0; left: 0; width: 100%; height: 100%;
                    display: flex; align-items: center; justify-content: center;
                    background: rgba(0,0,0,0.7); color: #fff;
                    flex-direction: column; gap: 8px; font-size: 13px; pointer-events: none;
                }
                .status-badge {
                    position: absolute; top: 10px; right: 10px;
                    padding: 4px 12px; border-radius: 20px;
                    font-size: 12px; font-weight: 600; z-index: 2;
                }
                .status-live { background: #ef4444; color: #fff; animation: pulse 2s infinite; }
                .status-connecting { background: #f59e0b; color: #fff; }
                .status-disconnected { background: #6b7280; color: #fff; }
                .student-info { padding: 12px; }
                .student-name { font-weight: 700; font-size: 16px; margin: 0; color: #1e293b; }
                .student-id { font-size: 13px; color: #64748b; margin-top: 2px; }
                @keyframes pulse { 0% { opacity:1; } 50% { opacity:0.6; } 100% { opacity:1; } }
            `}} />
        </div>
    );
};

const StudentVideoCard = ({ id, student, videoRefCallback }) => {
    const showOverlay = student.status !== 'Live';

    return (
        <div className="student-card">
            <div className="video-container">
                <span className={`status-badge status-${student.status?.toLowerCase()}`}>
                    {student.status}
                </span>
                {/* Always render video so srcObject can be assigned immediately in ontrack */}
                <video
                    ref={videoRefCallback}
                    autoPlay
                    playsInline
                    muted
                    className="student-video"
                />
                {showOverlay && (
                    <div className="video-overlay">
                        <div className="spinner-border spinner-border-sm" role="status" />
                        <div>Waiting for stream...</div>
                    </div>
                )}
            </div>
            <div className="student-info">
                <p className="student-name text-truncate">{student.name || 'Unknown Student'}</p>
                <p className="student-id">ID: {id}</p>
            </div>
        </div>
    );
};

export default InstitutionMonitoringMain;
